// Canaux de version PWA ciblés par utilisateur (messika-pwa#34).
// Logique pure, sans dépendance React.

export const CHANNEL_COOKIE_NAME = 'ome_channel'
export const LEGACY_CHANNEL_COOKIE_NAME = 'ome_canal'   // ancien nom (messika-pwa#34), à supprimer
export const CHANNEL_COOKIE_MAX_AGE = 10 * 365 * 24 * 3600   // ~10 ans, en secondes

/** Pref AppWrite `pwa_canal` → entier ≥ 1. Absente / invalide → 1. */
export function channelFromPref(value: unknown): number {
   if (typeof value !== 'number' && typeof value !== 'string') return 1
   if (typeof value === 'string' && value.trim() === '') return 1
   const n = Number(value)
   return Number.isInteger(n) && n >= 1 ? n : 1
}

/** 2 → "chan2" */
export function channelName(n: number): string {
   return 'chan' + n
}

/** Vrai si le build courant n'est pas celui du canal voulu. */
export function needsChannelSwitch(buildChannel: number, wantedChannel: number): boolean {
   return buildChannel !== wantedChannel
}

/** Chaîne à affecter à document.cookie pour aiguiller nginx. */
export function channelCookieString(n: number): string {
   return `${CHANNEL_COOKIE_NAME}=${n}; path=/; max-age=${CHANNEL_COOKIE_MAX_AGE}; SameSite=Strict; Secure`
}

/** Chaîne supprimant l'ancien cookie `ome_canal`. */
export function legacyChannelCookieDeletion(): string {
   return `${LEGACY_CHANNEL_COOKIE_NAME}=; path=/; max-age=0`
}

/**
 * Décide s'il faut tenter la bascule. `lastTried` = dernier canal pour lequel une bascule a déjà
 * été tentée (sessionStorage) : si le build chargé après cette tentative n'est toujours pas
 * le canal voulu, ce canal n'est pas déployé — on ne reboucle pas.
 */
export function shouldAttemptSwitch(buildChannel: number, wantedChannel: number, lastTried: number | null): boolean {
   return needsChannelSwitch(buildChannel, wantedChannel) && lastTried !== wantedChannel
}

export type SwitchOutcome = 'defer' | 'abandon' | 'wait'

/**
 * Décision après une tentative de bascule (messika-pwa#36), d'après l'état de la registration :
 * absente → 'defer' (rejouer quand elle arrive) ; sans `installing` ni `waiting` → 'abandon'
 * (rien de nouveau servi, canal non déployé) ; sinon → 'wait' (attendre controllerchange).
 */
export function decideSwitchOutcome(reg: { installing?: unknown, waiting?: unknown } | null | undefined): SwitchOutcome {
   if (!reg) return 'defer'
   return reg.installing || reg.waiting ? 'wait' : 'abandon'
}

export type CheckpointAction = 'switch' | 'install' | 'none'

export interface CheckpointDecision {
   action: CheckpointAction
   /** Vrai s'il faut effacer la mémoire anti-boucle (canal atteint). */
   clearTried: boolean
}

/**
 * Décision du point de contrôle (messika-pwa#39). Sans session, la pref est inconnue : jamais de
 * bascule, mémoire anti-boucle intacte ; seule l'installation d'une nouvelle version du même canal reste possible.
 */
export function decideCheckpoint(p: {
   buildChannel: number, wantedChannel: number, lastTried: number | null, hasSession: boolean, updateAvailable: boolean,
}): CheckpointDecision {
   if (!p.hasSession) return { action: p.updateAvailable ? 'install' : 'none', clearTried: false }
   const clearTried = p.wantedChannel === p.buildChannel
   if (shouldAttemptSwitch(p.buildChannel, p.wantedChannel, p.lastTried)) return { action: 'switch', clearTried }
   return { action: p.updateAvailable ? 'install' : 'none', clearTried }
}
